import { useEffect, useState } from "react";
import { isTimestampMode, type TimestampMode } from "@dayvise/react-ui";

/** Personal per-client display preference; the transcript itself never changes. */
export function timestampModeKey(client: "web" | "desktop" | "android", workspaceId: string, userId: string): string {
  return `dayvise.timestamp-mode:${JSON.stringify([client, workspaceId, userId])}`;
}

function read(key: string | null): TimestampMode {
  try {
    const stored = key === null ? null : localStorage.getItem(key);
    return isTimestampMode(stored) ? stored : "relative";
  } catch {
    return "relative";
  }
}

export function useTimestampMode(
  client: "web" | "desktop",
  workspaceId: string | null,
  userId: string | null,
): readonly [TimestampMode, (mode: TimestampMode) => boolean] {
  const key = workspaceId && userId ? timestampModeKey(client, workspaceId, userId) : null;
  const [value, setValue] = useState(() => ({ key, mode: read(key) }));
  // Read the new scope on its first render; never flash the prior user's preference.
  const mode = value.key === key ? value.mode : read(key);
  useEffect(() => {
    setValue({ key, mode: read(key) });
    const refresh = (event: StorageEvent) => { if (event.key === key || event.key === null) setValue({ key, mode: read(key) }); };
    window.addEventListener("storage", refresh);
    return () => window.removeEventListener("storage", refresh);
  }, [key]);
  return [mode, (next) => {
    setValue({ key, mode: next });
    if (!key) return false;
    try { localStorage.setItem(key, next); return true; } catch { return false; }
  }] as const;
}
